/**
 * ChatWidget - Embeddable web chat widget
 *
 * Provides a click-to-chat modal popup that can be attached to any HTML element.
 * Designed for easy integration with minimal configuration.
 *
 * Features:
 * - Click any element to open chat popup
 * - Self-contained CSS injection
 * - Real-time message streaming
 * - Typing indicators
 * - Mobile responsive
 * - Theme support (light/dark)
 * - Keyboard shortcuts
 * - Accessible (ARIA labels)
 *
 * Usage:
 *   ChatWidget.init({
 *     selector: '#chat-button',
 *     serverUrl: 'https://your-server.com',
 *     botNumber: '8065250225'
 *   });
 */

import { ChatClient } from '../clients/ChatClient.js';
import { VoiceMateClient } from '../core/VoiceMateClient.js';
import { Logger } from '../utils/Logger.js';
import { ValidationError } from '../errors/VoiceMateErrors.js';

export class ChatWidget {
  // Static registry to track initialized widgets by selector (singleton pattern)
  static instances = new Map();

  constructor(config) {
    this.config = this.validateConfig(config);
    this.logger = new Logger(this.config.logLevel);
    this.client = null;
    this.voiceClient = null;
    this.state = {
      isOpen: false,
      messages: [],
      currentChunk: ''
    };
    this.elements = {};
    this.triggerElements = [];  // Store trigger elements for cleanup
    this.clickHandler = null;   // Store click handler for removal
    this.initialize();
  }

  /**
   * Static factory method for initialization
   * Implements singleton pattern per selector to prevent duplicate widgets
   * @param {Object} config - Widget configuration
   * @returns {ChatWidget} Widget instance
   */
  static init(config) {
    const selector = config?.selector;

    // If widget already exists for this selector, return existing instance
    // This prevents duplicate widgets when React StrictMode double-invokes effects
    if (selector && ChatWidget.instances.has(selector)) {
      return ChatWidget.instances.get(selector);
    }

    const widget = new ChatWidget(config);

    // Store reference in registry
    if (selector) {
      ChatWidget.instances.set(selector, widget);
    }

    return widget;
  }

  /**
   * Validate configuration
   * @param {Object} config - Configuration object
   * @returns {Object} Validated config with defaults
   * @private
   */
  validateConfig(config) {
    if (!config) {
      throw new ValidationError('Configuration is required');
    }

    if (!config.selector) {
      throw new ValidationError('selector is required');
    }

    if (!config.serverUrl) {
      throw new ValidationError('serverUrl is required');
    }

    if (!config.botNumber) {
      throw new ValidationError('botNumber is required');
    }

    return {
      // Required
      selector: config.selector,
      serverUrl: config.serverUrl,
      botNumber: config.botNumber,

      // Optional
      userNumber: config.userNumber || 'web_user',
      language: config.language || 'en',
      theme: config.theme || 'light',
      position: config.position || 'center',
      botName: config.botName || 'Chat Support',
      showTimestamps: config.showTimestamps !== false,
      logLevel: config.logLevel || 'info',
      primaryColor: config.primaryColor || '#667eea',

      // Voice call integration
      enableCall: config.enableCall || false,
      callButtonLabel: config.callButtonLabel || 'Start Voice Call',
      callAutoConnect: config.callAutoConnect !== false,
      callTheme: config.callTheme || config.theme || 'light',
      callPosition: config.callPosition || 'center'
    };
  }

  /**
   * Initialize widget
   * @private
   */
  initialize() {
    // Find trigger elements
    const triggers = document.querySelectorAll(this.config.selector);

    if (triggers.length === 0) {
      this.logger.warn(`No elements found for selector: ${this.config.selector}`);
      return;
    }

    // Create reusable click handler (stored for removal in destroy)
    this.clickHandler = (e) => {
      e.preventDefault();
      this.openWidget();
    };

    // Attach click handlers to all matching elements
    triggers.forEach(el => {
      el.addEventListener('click', this.clickHandler);
      el.style.cursor = 'pointer';
      this.triggerElements.push(el);  // Store for cleanup
      this.logger.debug(`Attached chat widget to element:`, el);
    });

    this.logger.info(`ChatWidget initialized on ${triggers.length} element(s)`);

    // Create modal popup (hidden initially)
    this.createChatModal();

    // Inject styles
    this.injectStyles();
  }

  /**
   * Create chat modal DOM structure
   * @private
   */
  createChatModal() {
    // Create modal container
    const modal = document.createElement('div');
    modal.id = 'chatmate-widget-modal';
    modal.className = `chatmate-modal chatmate-theme-${this.config.theme}`;
    modal.style.display = 'none';
    modal.innerHTML = `
      <div class="chatmate-modal-overlay"></div>
      <div class="chatmate-modal-content ${this.config.position}">
        <div class="chatmate-modal-header">
          <h3>${this.escapeHtml(this.config.botName)}</h3>
          <div class="chatmate-header-actions">
            ${this.config.enableCall ? `
              <button class="chatmate-call-btn" aria-label="${this.config.callButtonLabel}" title="${this.config.callButtonLabel}">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                </svg>
              </button>
            ` : ''}
            <button class="chatmate-close-btn" aria-label="Close chat">&times;</button>
          </div>
        </div>
        <div class="chatmate-modal-body">
          <div class="chatmate-status">
            <div class="chatmate-status-icon">
              <div class="chatmate-pulse"></div>
            </div>
            <p class="chatmate-status-text">Initializing...</p>
          </div>
          <div class="chatmate-messages" role="log" aria-live="polite" aria-atomic="false"></div>
          <div class="chatmate-input-area">
            <textarea
              class="chatmate-input"
              placeholder="Type a message..."
              aria-label="Message input"
              rows="1"
            ></textarea>
            <button class="chatmate-send" aria-label="Send message">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="chatmate-footer">
          <small>Powered by Voice Mate SDK</small>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    // Store element references
    this.elements.modal = modal;
    this.elements.overlay = modal.querySelector('.chatmate-modal-overlay');
    this.elements.closeBtn = modal.querySelector('.chatmate-close-btn');
    this.elements.messages = modal.querySelector('.chatmate-messages');
    this.elements.input = modal.querySelector('.chatmate-input');
    this.elements.sendBtn = modal.querySelector('.chatmate-send');
    this.elements.statusIcon = modal.querySelector('.chatmate-status-icon');
    this.elements.statusText = modal.querySelector('.chatmate-status-text');

    // Store call button reference if enabled
    if (this.config.enableCall) {
      this.elements.callBtn = modal.querySelector('.chatmate-call-btn');
    }

    // Attach event listeners
    this.attachEventListeners();
  }

  /**
   * Attach event listeners to modal elements
   * @private
   */
  attachEventListeners() {
    // Close button only - overlay click does NOT close
    this.elements.closeBtn.onclick = () => this.closeWidget();

    // Send button
    this.elements.sendBtn.onclick = () => this.sendMessage();

    // Input field - Enter to send, auto-resize
    this.elements.input.onkeydown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    };

    this.elements.input.oninput = () => {
      this.autoResizeTextarea();
    };

    // Call button (if enabled)
    if (this.config.enableCall && this.elements.callBtn) {
      this.elements.callBtn.onclick = () => this.startVoiceCall();
    }

    // Note: Popup only closes via close button click, not Escape key or overlay click
  }

  /**
   * Open chat widget
   * @returns {Promise<void>}
   */
  async openWidget() {
    if (this.state.isOpen) {
      this.logger.warn('Widget already open');
      return;
    }

    try {
      this.logger.info('Opening chat widget...');

      // Show modal
      this.elements.modal.style.display = 'block';
      this.state.isOpen = true;

      // Update status
      this.updateStatus('connecting', 'Connecting...');

      // Clean up existing client if any
      if (this.client) {
        this.logger.info('Cleaning up existing client...');
        try {
          await this.client.disconnect();
        } catch (err) {
          this.logger.warn('Error disconnecting old client:', err);
        }
        this.client = null;
      }

      // Create fresh client instance
      this.logger.info('Creating new ChatClient instance...');
      this.client = new ChatClient({
        serverUrl: this.config.serverUrl,
        botNumber: this.config.botNumber,
        userNumber: this.config.userNumber,
        language: this.config.language,
        logLevel: this.config.logLevel
      });

      // Setup event listeners
      this.setupClientListeners();

      // Connect
      await this.client.connect();

      // Focus input
      this.elements.input.focus();

    } catch (error) {
      this.logger.error('Failed to open widget:', error);
      this.updateStatus('error', 'Connection Error');
      this.addSystemMessage(`Failed to connect: ${error.message}`);
    }
  }

  /**
   * Close chat widget
   * @returns {Promise<void>}
   */
  async closeWidget() {
    this.logger.info('Closing chat widget...');

    try {
      // End voice call if active
      if (this.voiceClient) {
        try {
          await this.endVoiceCall();
        } catch (error) {
          this.logger.error('Error ending voice call during close:', error);
        }
      }

      // Disconnect client
      if (this.client) {
        try {
          await this.client.disconnect();
        } catch (error) {
          this.logger.error('Error disconnecting client:', error);
        }
        this.client = null;
      }
    } catch (error) {
      this.logger.error('Error during widget close:', error);
    } finally {
      // Always reset UI
      this.elements.modal.style.display = 'none';
      if (this.elements.voiceModal) {
        this.elements.voiceModal.style.display = 'none';
      }
      this.state.isOpen = false;
      this.state.messages = [];
      this.state.currentChunk = '';
      this.elements.messages.innerHTML = '';
      this.elements.input.value = '';
      this.elements.input.style.height = 'auto';
      this.updateStatus('disconnected', 'Offline');
    }
  }

  /**
   * Setup client event listeners
   * @private
   */
  setupClientListeners() {
    this.client.on('connected', () => {
      this.logger.info('Chat client connected');
      this.updateStatus('connected', 'Online');
    });

    this.client.on('disconnected', () => {
      this.logger.info('Chat client disconnected');
      this.updateStatus('disconnected', 'Offline');
    });

    this.client.on('typing', () => {
      this.logger.debug('Bot is typing...');
      this.showTypingIndicator();
    });

    this.client.on('chunk', (text) => {
      this.handleChunk(text);
    });

    this.client.on('message', (text) => {
      this.handleMessageComplete(text);
    });

    this.client.on('error', (error) => {
      this.logger.error('Chat client error:', error);
      this.addSystemMessage(`Error: ${error.message}`);
    });
  }

  /**
   * Send message
   */
  sendMessage() {
    const text = this.elements.input.value.trim();

    if (!text) {
      this.logger.debug('Empty message, not sending');
      return;
    }

    if (!this.client) {
      this.logger.warn('Cannot send message: client not initialized');
      this.addSystemMessage('Not connected. Please try again.');
      return;
    }

    try {
      // Add to UI immediately
      this.addMessage('user', text);

      // Send via client
      this.client.sendMessage(text);

      // Clear input
      this.elements.input.value = '';
      this.elements.input.style.height = 'auto';

      this.logger.info('Message sent:', text.substring(0, 50));

    } catch (error) {
      this.logger.error('Failed to send message:', error);
      this.addSystemMessage('Failed to send message. Please try again.');
    }
  }

  /**
   * Add message to UI
   * @param {string} role - 'user' or 'assistant'
   * @param {string} text - Message text
   * @private
   */
  addMessage(role, text) {
    const msg = document.createElement('div');
    msg.className = `chatmate-message chatmate-message-${role}`;

    const content = document.createElement('div');
    content.className = 'chatmate-message-content';
    content.textContent = text;
    msg.appendChild(content);

    if (this.config.showTimestamps) {
      const timestamp = document.createElement('div');
      timestamp.className = 'chatmate-timestamp';
      timestamp.textContent = this.formatTime(Date.now());
      msg.appendChild(timestamp);
    }

    this.elements.messages.appendChild(msg);
    this.scrollToBottom();

    this.state.messages.push({
      role,
      text,
      timestamp: Date.now()
    });
  }

  /**
   * Add system message (info/error)
   * @param {string} text - Message text
   * @private
   */
  addSystemMessage(text) {
    const msg = document.createElement('div');
    msg.className = 'chatmate-message chatmate-message-system';
    msg.textContent = text;
    this.elements.messages.appendChild(msg);
    this.scrollToBottom();
  }

  /**
   * Show typing indicator
   * @private
   */
  showTypingIndicator() {
    // Remove existing indicator if any
    const existing = this.elements.messages.querySelector('.chatmate-typing-indicator');
    if (existing) {
      existing.remove();
    }

    const indicator = document.createElement('div');
    indicator.className = 'chatmate-typing-indicator';
    indicator.innerHTML = `
      <div class="chatmate-typing-dot"></div>
      <div class="chatmate-typing-dot"></div>
      <div class="chatmate-typing-dot"></div>
    `;
    this.elements.messages.appendChild(indicator);
    this.scrollToBottom();
  }

  /**
   * Handle streaming chunk
   * @param {string} text - Chunk text
   * @private
   */
  handleChunk(text) {
    this.state.currentChunk += text;

    // Remove typing indicator
    const indicator = this.elements.messages.querySelector('.chatmate-typing-indicator');
    if (indicator) {
      indicator.remove();
    }

    // Update or create streaming message
    let streamMsg = this.elements.messages.querySelector('.chatmate-message-streaming');
    if (!streamMsg) {
      streamMsg = document.createElement('div');
      streamMsg.className = 'chatmate-message chatmate-message-assistant chatmate-message-streaming';

      const content = document.createElement('div');
      content.className = 'chatmate-message-content';
      streamMsg.appendChild(content);

      this.elements.messages.appendChild(streamMsg);
    }

    streamMsg.querySelector('.chatmate-message-content').textContent = this.state.currentChunk;
    this.scrollToBottom();
  }

  /**
   * Handle message completion
   * @param {string} fullText - Complete message text
   * @private
   */
  handleMessageComplete(fullText) {
    this.logger.debug('Message complete:', fullText.substring(0, 50));

    // Remove streaming class
    const streamMsg = this.elements.messages.querySelector('.chatmate-message-streaming');
    if (streamMsg) {
      streamMsg.classList.remove('chatmate-message-streaming');

      if (this.config.showTimestamps) {
        const timestamp = document.createElement('div');
        timestamp.className = 'chatmate-timestamp';
        timestamp.textContent = this.formatTime(Date.now());
        streamMsg.appendChild(timestamp);
      }
    }

    this.state.currentChunk = '';
    this.state.messages.push({
      role: 'assistant',
      text: fullText,
      timestamp: Date.now()
    });
  }

  /**
   * Update status indicator
   * @param {string} state - Connection state
   * @param {string} text - Status text
   * @private
   */
  updateStatus(state, text) {
    this.elements.statusText.textContent = text;

    // Update pulse color based on state
    const pulse = this.elements.statusIcon.querySelector('.chatmate-pulse');
    const colors = {
      'connecting': '#f59e0b',
      'connected': '#10b981',
      'disconnected': '#6b7280',
      'error': '#ef4444'
    };

    if (pulse) {
      pulse.style.background = colors[state] || '#10b981';
    }
  }

  /**
   * Scroll messages to bottom
   * @private
   */
  scrollToBottom() {
    this.elements.messages.scrollTop = this.elements.messages.scrollHeight;
  }

  /**
   * Auto-resize textarea
   * @private
   */
  autoResizeTextarea() {
    const textarea = this.elements.input;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  }

  /**
   * Format timestamp
   * @param {number} timestamp - Unix timestamp
   * @returns {string} Formatted time
   * @private
   */
  formatTime(timestamp) {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   * @private
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Inject widget styles
   * @private
   */
  injectStyles() {
    // Don't inject if already exists
    if (document.getElementById('chatmate-widget-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'chatmate-widget-styles';
    style.textContent = `
      /* Modal Container */
      .chatmate-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      }

      /* Overlay */
      .chatmate-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
      }

      /* Modal Content */
      .chatmate-modal-content {
        position: absolute;
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        max-width: 400px;
        width: 90%;
        height: 600px;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        animation: chatmate-slide-up 0.3s ease-out;
      }

      .chatmate-modal-content.center {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .chatmate-modal-content.bottom-right {
        bottom: 20px;
        right: 20px;
        transform: none;
      }

      @keyframes chatmate-slide-up {
        from {
          opacity: 0;
          transform: translate(-50%, -40%);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%);
        }
      }

      /* Header */
      .chatmate-modal-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
      }

      .chatmate-modal-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #111827;
      }

      .chatmate-close-btn {
        background: none;
        border: none;
        font-size: 28px;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        border-radius: 6px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }

      .chatmate-close-btn:hover {
        background: #f3f4f6;
        color: #111827;
      }

      /* Header Actions */
      .chatmate-header-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .chatmate-call-btn {
        background: none;
        border: none;
        color: #667eea;
        cursor: pointer;
        padding: 8px;
        border-radius: 6px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .chatmate-call-btn:hover {
        background: #f3f4f6;
        color: #764ba2;
      }

      .chatmate-call-btn svg {
        width: 20px;
        height: 20px;
      }

      /* Modal Body */
      .chatmate-modal-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 16px 0;
        min-height: 0;
      }

      /* Status Indicator */
      .chatmate-status {
        text-align: center;
        margin-bottom: 16px;
        padding: 0 24px;
        flex-shrink: 0;
      }

      .chatmate-status-icon {
        width: 40px;
        height: 40px;
        margin: 0 auto 8px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .chatmate-pulse {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: #10b981;
        position: relative;
      }

      .chatmate-pulse::before,
      .chatmate-pulse::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: inherit;
        opacity: 0.6;
        animation: chatmate-pulse 2s ease-out infinite;
      }

      .chatmate-pulse::after {
        animation-delay: 1s;
      }

      @keyframes chatmate-pulse {
        0% {
          transform: scale(1);
          opacity: 0.6;
        }
        100% {
          transform: scale(1.5);
          opacity: 0;
        }
      }

      .chatmate-status-text {
        font-size: 14px;
        color: #374151;
        margin: 0;
      }

      /* Messages */
      .chatmate-messages {
        flex: 1;
        overflow-y: auto;
        padding: 0 24px 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-height: 0;
      }

      .chatmate-messages::-webkit-scrollbar {
        width: 6px;
      }

      .chatmate-messages::-webkit-scrollbar-track {
        background: transparent;
      }

      .chatmate-messages::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 3px;
      }

      .chatmate-messages::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
      }

      .chatmate-message {
        display: flex;
        flex-direction: column;
        max-width: 80%;
        animation: chatmate-message-appear 0.2s ease-out;
      }

      @keyframes chatmate-message-appear {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .chatmate-message-user {
        align-self: flex-end;
      }

      .chatmate-message-assistant {
        align-self: flex-start;
      }

      .chatmate-message-system {
        align-self: center;
        font-size: 12px;
        color: #9ca3af;
        padding: 8px 12px;
        background: #f9fafb;
        border-radius: 12px;
        max-width: 90%;
        text-align: center;
      }

      .chatmate-message-content {
        padding: 12px 16px;
        border-radius: 18px;
        word-wrap: break-word;
        white-space: pre-wrap;
        line-height: 1.5;
      }

      .chatmate-message-user .chatmate-message-content {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border-bottom-right-radius: 6px;
      }

      .chatmate-message-assistant .chatmate-message-content {
        background: #f3f4f6;
        color: #111827;
        border-bottom-left-radius: 6px;
      }

      .chatmate-timestamp {
        font-size: 11px;
        color: #9ca3af;
        margin-top: 4px;
        padding: 0 4px;
      }

      .chatmate-message-user .chatmate-timestamp {
        text-align: right;
      }

      .chatmate-message-assistant .chatmate-timestamp {
        text-align: left;
      }

      /* Typing Indicator */
      .chatmate-typing-indicator {
        display: flex;
        gap: 4px;
        padding: 12px 16px;
        background: #f3f4f6;
        border-radius: 18px;
        width: fit-content;
        align-self: flex-start;
      }

      .chatmate-typing-dot {
        width: 8px;
        height: 8px;
        background: #9ca3af;
        border-radius: 50%;
        animation: chatmate-typing 1.4s infinite;
      }

      .chatmate-typing-dot:nth-child(2) {
        animation-delay: 0.2s;
      }

      .chatmate-typing-dot:nth-child(3) {
        animation-delay: 0.4s;
      }

      @keyframes chatmate-typing {
        0%, 60%, 100% {
          transform: translateY(0);
        }
        30% {
          transform: translateY(-10px);
        }
      }

      /* Input Area */
      .chatmate-input-area {
        padding: 16px 24px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        gap: 12px;
        align-items: flex-end;
        flex-shrink: 0;
      }

      .chatmate-input {
        flex: 1;
        padding: 12px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        resize: none;
        font-family: inherit;
        font-size: 14px;
        line-height: 1.5;
        max-height: 120px;
        overflow-y: auto;
        transition: border-color 0.2s;
      }

      .chatmate-input:focus {
        outline: none;
        border-color: #667eea;
      }

      .chatmate-input::placeholder {
        color: #9ca3af;
      }

      .chatmate-send {
        padding: 12px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 44px;
        height: 44px;
      }

      .chatmate-send:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      }

      .chatmate-send:active {
        transform: translateY(0);
      }

      .chatmate-send svg {
        width: 20px;
        height: 20px;
      }

      /* Footer */
      .chatmate-footer {
        padding: 12px 24px;
        background: #f9fafb;
        border-top: 1px solid #e5e7eb;
        text-align: center;
        border-radius: 0 0 16px 16px;
        flex-shrink: 0;
      }

      .chatmate-footer small {
        color: #9ca3af;
        font-size: 11px;
      }

      /* Dark Theme */
      .chatmate-theme-dark .chatmate-modal-content {
        background: #1f2937;
      }

      .chatmate-theme-dark .chatmate-modal-header {
        border-bottom-color: #374151;
      }

      .chatmate-theme-dark .chatmate-modal-header h3 {
        color: #f9fafb;
      }

      .chatmate-theme-dark .chatmate-close-btn {
        color: #9ca3af;
      }

      .chatmate-theme-dark .chatmate-close-btn:hover {
        background: #374151;
        color: #f9fafb;
      }

      .chatmate-theme-dark .chatmate-status-text {
        color: #d1d5db;
      }

      .chatmate-theme-dark .chatmate-message-assistant .chatmate-message-content {
        background: #374151;
        color: #f9fafb;
      }

      .chatmate-theme-dark .chatmate-message-system {
        background: #374151;
        color: #9ca3af;
      }

      .chatmate-theme-dark .chatmate-typing-indicator {
        background: #374151;
      }

      .chatmate-theme-dark .chatmate-input-area {
        border-top-color: #374151;
      }

      .chatmate-theme-dark .chatmate-input {
        background: #374151;
        border-color: #4b5563;
        color: #f9fafb;
      }

      .chatmate-theme-dark .chatmate-input::placeholder {
        color: #6b7280;
      }

      .chatmate-theme-dark .chatmate-input:focus {
        border-color: #667eea;
      }

      .chatmate-theme-dark .chatmate-footer {
        background: #111827;
        border-top-color: #374151;
      }

      /* Mobile */
      @media (max-width: 768px) {
        .chatmate-modal-content {
          width: 100vw !important;
          height: 100vh !important;
          max-width: none !important;
          max-height: none !important;
          border-radius: 0 !important;
          top: 0 !important;
          left: 0 !important;
          bottom: 0 !important;
          right: 0 !important;
          transform: none !important;
        }

        @keyframes chatmate-slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      }

      /* Voice Modal Styles */
      .voicemate-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1000000;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      }

      .voicemate-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
      }

      .voicemate-modal-content {
        position: absolute;
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        width: 400px;
        max-width: 90%;
        display: flex;
        flex-direction: column;
        animation: voicemate-slide-up 0.3s ease-out;
      }

      .voicemate-modal-content.center {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .voicemate-modal-content.bottom-right {
        bottom: 20px;
        right: 20px;
        transform: none;
      }

      @keyframes voicemate-slide-up {
        from {
          opacity: 0;
          transform: translate(-50%, -40%);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%);
        }
      }

      .voicemate-modal-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .voicemate-modal-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #111827;
      }

      .voicemate-close-btn {
        background: none;
        border: none;
        font-size: 28px;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        border-radius: 6px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }

      .voicemate-close-btn:hover {
        background: #f3f4f6;
        color: #111827;
      }

      .voicemate-modal-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 24px;
      }

      .voicemate-status {
        text-align: center;
        margin-bottom: 32px;
      }

      .voicemate-status-icon {
        width: 60px;
        height: 60px;
        margin: 0 auto 16px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .voicemate-pulse {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background: #10b981;
        position: relative;
      }

      .voicemate-pulse::before,
      .voicemate-pulse::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: inherit;
        opacity: 0.6;
        animation: voicemate-pulse 2s ease-out infinite;
      }

      .voicemate-pulse::after {
        animation-delay: 1s;
      }

      @keyframes voicemate-pulse {
        0% {
          transform: scale(1);
          opacity: 0.6;
        }
        100% {
          transform: scale(1.5);
          opacity: 0;
        }
      }

      .voicemate-status-text {
        font-size: 16px;
        color: #374151;
        margin: 0;
        font-weight: 500;
      }

      .voicemate-controls {
        display: flex;
        gap: 16px;
        justify-content: center;
      }

      .voicemate-mute-btn,
      .voicemate-end-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 16px 24px;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        background: white;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 14px;
        font-weight: 500;
        color: #374151;
      }

      .voicemate-mute-btn:disabled,
      .voicemate-end-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .voicemate-mute-btn:not(:disabled):hover {
        border-color: #667eea;
        background: #f3f4f6;
      }

      .voicemate-end-btn:not(:disabled):hover {
        border-color: #ef4444;
        background: #fef2f2;
        color: #ef4444;
      }

      .voicemate-mute-btn.muted {
        border-color: #ef4444;
        background: #fef2f2;
        color: #ef4444;
      }

      .voicemate-footer {
        padding: 12px 24px;
        background: #f9fafb;
        border-top: 1px solid #e5e7eb;
        text-align: center;
        border-radius: 0 0 16px 16px;
      }

      .voicemate-footer small {
        color: #9ca3af;
        font-size: 11px;
      }

      /* Dark theme for voice modal */
      .voicemate-theme-dark .voicemate-modal-content {
        background: #1f2937;
      }

      .voicemate-theme-dark .voicemate-modal-header {
        border-bottom-color: #374151;
      }

      .voicemate-theme-dark .voicemate-modal-header h3 {
        color: #f9fafb;
      }

      .voicemate-theme-dark .voicemate-close-btn {
        color: #9ca3af;
      }

      .voicemate-theme-dark .voicemate-close-btn:hover {
        background: #374151;
        color: #f9fafb;
      }

      .voicemate-theme-dark .voicemate-status-text {
        color: #d1d5db;
      }

      .voicemate-theme-dark .voicemate-mute-btn,
      .voicemate-theme-dark .voicemate-end-btn {
        background: #374151;
        border-color: #4b5563;
        color: #d1d5db;
      }

      .voicemate-theme-dark .voicemate-mute-btn:not(:disabled):hover {
        background: #4b5563;
        border-color: #667eea;
      }

      .voicemate-theme-dark .voicemate-end-btn:not(:disabled):hover {
        background: #7f1d1d;
        border-color: #ef4444;
        color: #fca5a5;
      }

      .voicemate-theme-dark .voicemate-footer {
        background: #111827;
        border-top-color: #374151;
      }
    `;
    document.head.appendChild(style);
    this.logger.debug('Styles injected');
  }

  /**
   * Start voice call
   * @returns {Promise<void>}
   */
  async startVoiceCall() {
    this.logger.info('Starting voice call from chat widget...');

    try {
      // Create voice modal
      this.createVoiceModal();

      // Show voice modal
      this.elements.voiceModal.style.display = 'block';
      this.updateVoiceStatus('connecting', 'Connecting...');

      // Create voice client
      this.voiceClient = new VoiceMateClient({
        serverUrl: this.config.serverUrl,
        botNumber: this.config.botNumber,
        userNumber: this.config.userNumber,
        language: this.config.language,
        logLevel: this.config.logLevel
      });

      // Setup voice event listeners
      this.setupVoiceListeners();

      // Connect and start call
      await this.voiceClient.connect();

      if (this.config.callAutoConnect) {
        await this.voiceClient.startCall();
      }

    } catch (error) {
      this.logger.error('Failed to start voice call:', error);
      this.updateVoiceStatus('error', `Error: ${error.message}`);
    }
  }

  /**
   * Create voice modal DOM structure
   * @private
   */
  createVoiceModal() {
    if (this.elements.voiceModal) return;  // Already created

    const modal = document.createElement('div');
    modal.className = `voicemate-modal voicemate-theme-${this.config.callTheme}`;
    modal.style.display = 'none';
    modal.innerHTML = `
      <div class="voicemate-modal-overlay"></div>
      <div class="voicemate-modal-content ${this.config.callPosition}">
        <div class="voicemate-modal-header">
          <h3>Voice Call</h3>
          <button class="voicemate-close-btn" aria-label="Close">&times;</button>
        </div>
        <div class="voicemate-modal-body">
          <div class="voicemate-status">
            <div class="voicemate-status-icon">
              <div class="voicemate-pulse"></div>
            </div>
            <p class="voicemate-status-text">Initializing...</p>
          </div>
          <div class="voicemate-controls">
            <button class="voicemate-mute-btn" aria-label="Toggle mute" disabled>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                <path fill="currentColor" d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
              </svg>
              <span>Mute</span>
            </button>
            <button class="voicemate-end-btn" aria-label="End call" disabled>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.68-1.36-2.66-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
              </svg>
              <span>End Call</span>
            </button>
          </div>
        </div>
        <div class="voicemate-footer">
          <small>Voice Call via Voice Mate SDK</small>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    // Store references
    this.elements.voiceModal = modal;
    this.elements.voiceOverlay = modal.querySelector('.voicemate-modal-overlay');
    this.elements.voiceCloseBtn = modal.querySelector('.voicemate-close-btn');
    this.elements.voiceMuteBtn = modal.querySelector('.voicemate-mute-btn');
    this.elements.voiceEndBtn = modal.querySelector('.voicemate-end-btn');
    this.elements.voiceStatusIcon = modal.querySelector('.voicemate-status-icon');
    this.elements.voiceStatusText = modal.querySelector('.voicemate-status-text');

    // Attach listeners - close button only, overlay click does NOT close
    this.elements.voiceCloseBtn.onclick = () => this.endVoiceCall();
    this.elements.voiceMuteBtn.onclick = () => this.toggleMute();
    this.elements.voiceEndBtn.onclick = () => this.endVoiceCall();
  }

  /**
   * Setup voice client event listeners
   * @private
   */
  setupVoiceListeners() {
    this.voiceClient.on('connected', () => {
      this.updateVoiceStatus('connected', 'Connected');
    });

    // Note: VoiceMateClient uses 'call:started' (colon) not 'call_started' (underscore)
    this.voiceClient.on('call:started', () => {
      this.logger.info('Voice call started - enabling controls');
      this.updateVoiceStatus('in_call', 'Call Active');
      this.elements.voiceMuteBtn.disabled = false;
      this.elements.voiceEndBtn.disabled = false;
    });

    this.voiceClient.on('call:ended', () => {
      this.logger.info('Voice call ended - disabling controls');
      this.updateVoiceStatus('ended', 'Call Ended');
      this.elements.voiceMuteBtn.disabled = true;
      this.elements.voiceEndBtn.disabled = true;
    });

    this.voiceClient.on('error', (error) => {
      this.logger.error('Voice call error:', error);
      this.updateVoiceStatus('error', `Error: ${error.message}`);
    });
  }

  /**
   * Toggle mute/unmute
   * @returns {Promise<void>}
   */
  async toggleMute() {
    if (!this.voiceClient) return;

    try {
      const muted = await this.voiceClient.toggleMute();

      if (muted) {
        this.elements.voiceMuteBtn.classList.add('muted');
        this.elements.voiceMuteBtn.querySelector('span').textContent = 'Unmute';
      } else {
        this.elements.voiceMuteBtn.classList.remove('muted');
        this.elements.voiceMuteBtn.querySelector('span').textContent = 'Mute';
      }
    } catch (error) {
      this.logger.error('Failed to toggle mute:', error);
    }
  }

  /**
   * End voice call
   * @returns {Promise<void>}
   */
  async endVoiceCall() {
    this.logger.info('Ending voice call...');

    try {
      if (this.voiceClient) {
        await this.voiceClient.endCall();
        await this.voiceClient.disconnect();
        this.voiceClient = null;
      }
    } catch (error) {
      this.logger.error('Error ending voice call:', error);
    } finally {
      if (this.elements.voiceModal) {
        this.elements.voiceModal.style.display = 'none';
      }
    }
  }

  /**
   * Update voice call status
   * @param {string} state - Connection state
   * @param {string} text - Status text
   * @private
   */
  updateVoiceStatus(state, text) {
    this.elements.voiceStatusText.textContent = text;

    const pulse = this.elements.voiceStatusIcon.querySelector('.voicemate-pulse');
    const colors = {
      'connecting': '#f59e0b',
      'connected': '#10b981',
      'in_call': '#3b82f6',
      'ended': '#6b7280',
      'error': '#ef4444'
    };

    if (pulse) {
      pulse.style.background = colors[state] || '#10b981';
    }
  }

  /**
   * Destroy widget and cleanup
   */
  destroy() {
    this.logger.info('Destroying chat widget...');

    // Remove click handlers from trigger elements
    if (this.clickHandler && this.triggerElements.length > 0) {
      this.triggerElements.forEach(el => {
        el.removeEventListener('click', this.clickHandler);
      });
      this.triggerElements = [];
      this.clickHandler = null;
    }

    if (this.client) {
      this.client.disconnect();
      this.client = null;
    }

    if (this.voiceClient) {
      this.voiceClient.disconnect();
      this.voiceClient = null;
    }

    if (this.elements.modal) {
      this.elements.modal.remove();
    }

    if (this.elements.voiceModal) {
      this.elements.voiceModal.remove();
    }

    const styles = document.getElementById('chatmate-widget-styles');
    if (styles) {
      styles.remove();
    }

    // Remove from static registry
    if (this.config?.selector) {
      ChatWidget.instances.delete(this.config.selector);
    }
  }
}
